/**
 * Step 3: Configure Quantities
 * Single item: Normal form
 * Multiple items: Table with shared vendor/invoice/notes below
 */

import React, { useState } from 'react';
import {
  Box, Typography, TextField, Grid, useTheme, alpha, Button,
  Dialog, DialogTitle, DialogContent, IconButton, InputAdornment, Paper,
  Table, TableHead, TableBody, TableRow, TableCell
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

export default function StepConfigureQuantity({
  adjustmentType,
  formData,
  onFormChange,
  suppliers,
  config,
  selectedItems,
  itemsConfig,
  onItemsConfigChange
}) {
  const theme = useTheme();
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorSearch, setVendorSearch] = useState('');

  const handleChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  const handleItemConfigChange = (itemId, field, value) => {
    onItemsConfigChange({
      ...itemsConfig,
      [itemId]: {
        ...(itemsConfig[itemId] || {}),
        [field]: value
      }
    });
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name?.toLowerCase().includes(vendorSearch.toLowerCase())
  );

  // ── Non-product lot helpers ───────────────────────────────────────────────
  const updateNonProductLot = (idx, field, value) => {
    const lots = [...(formData.nonProductLots || [])];
    lots[idx] = { ...lots[idx], [field]: value };
    onFormChange({ ...formData, nonProductLots: lots });
  };
  const addNonProductLot = () => {
    const firstCost = formData.nonProductLots?.[0]?.unitCost || '';
    onFormChange({
      ...formData,
      nonProductLots: [...(formData.nonProductLots || []),
        { quantity: '', lotNumber: '', batchNumber: '', unitCost: firstCost, expiryDate: '' }]
    });
  };
  const removeNonProductLot = (idx) => {
    onFormChange({ ...formData, nonProductLots: (formData.nonProductLots || []).filter((_, i) => i !== idx) });
  };

  const updateItemLot = (itemId, lotIdx, field, value) => {
    const cfg = itemsConfig[itemId] || { lots: [] };
    const lots = [...(cfg.lots || [])];
    lots[lotIdx] = { ...lots[lotIdx], [field]: value };
    onItemsConfigChange({ ...itemsConfig, [itemId]: { ...cfg, lots } });
  };
  const addItemLot = (item) => {
    const cfg = itemsConfig[item.id] || { lots: [] };
    const firstCost = cfg.lots?.[0]?.unitCost || item.unit_cost || item.cost || '';
    onItemsConfigChange({
      ...itemsConfig,
      [item.id]: { ...cfg, lots: [...(cfg.lots || []),
        { quantity: '', lotNumber: '', batchNumber: '', unitCost: firstCost.toString(), expiryDate: '' }] }
    });
  };
  const removeItemLot = (itemId, lotIdx) => {
    const cfg = itemsConfig[itemId] || { lots: [] };
    onItemsConfigChange({ ...itemsConfig, [itemId]: { ...cfg, lots: (cfg.lots || []).filter((_, i) => i !== lotIdx) } });
  };

  // Text field common styling
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(10px)',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: alpha(theme.palette.primary.main, 0.3),
        borderWidth: 1
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      }
    },
    '& .MuiInputLabel-root': {
      fontWeight: 500
    }
  };

  const isMultiItem = selectedItems.length > 1;

  // Chemical unit config (single item only)
  const isChemical = config.hasUnitOfMeasure;
  const chemItem = isChemical && selectedItems.length === 1 ? selectedItems[0] : null;
  const chemUnitType = chemItem?.primary_unit_type || config.unit || 'units';
  const chemUnitMeasure = chemItem?.primary_unit_measure || '';
  const chemUnitQtyPerUnit = chemItem?.primary_unit_quantity || null;
  const chemTotalAmount = chemUnitQtyPerUnit && formData.quantity
    ? (parseFloat(formData.quantity) || 0) * chemUnitQtyPerUnit
    : null;

  // Helper: singular label (e.g. "Drum" from "Drums")
  const singularUnitType = (t) => {
    if (!t) return '';
    const lower = t.toLowerCase();
    return (lower.endsWith('s') ? lower.slice(0, -1) : lower).replace(/^\w/, c => c.toUpperCase());
  };

  // Product unit config (pallet/case/unit)
  const isProduct = config.hasUnitConfig;
  const pQty = parseInt(formData.palletQty) || 0;
  const cQty = parseInt(formData.caseQty) || 0;
  const uQty = parseInt(formData.unitQty) || 0;
  const productUnitsPerCase   = parseInt(formData._unitsPerCase) || 1;
  const productCasesPerPallet = parseInt(formData._casesPerPallet) || 1;
  const productTotalBase = isProduct
    ? (pQty * productCasesPerPallet * productUnitsPerCase) + (cQty * productUnitsPerCase) + uQty
    : 0;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: isChemical && chemItem ? 2 : 4, color: 'text.secondary', fontSize: '1.05rem' }}>
        {adjustmentType === 'add' && 'Enter the quantity and details for the new stock'}
        {adjustmentType === 'usage' && 'Enter the quantity to remove from inventory'}
        {adjustmentType === 'transfer' && 'Enter the quantity to transfer'}
      </Typography>

      {/* CHEMICAL: Unit Configuration info card */}
      {isChemical && chemItem && (
        <Box sx={{
          mb: 4, p: 2.5, borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          display: 'flex', alignItems: 'center', gap: 3
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Unit Configuration
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.1rem' }}>
              {chemUnitQtyPerUnit
                ? `${chemUnitQtyPerUnit}${chemUnitMeasure} per ${chemUnitType}`
                : chemUnitType}
            </Typography>
            <Typography variant="body2" color="text.secondary">{chemItem.name}</Typography>
          </Box>
          {chemTotalAmount !== null && (
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Calculated Total
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main', fontSize: '1.1rem' }}>
                {chemTotalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}{chemUnitMeasure}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* PRODUCT: Unit Configuration info card */}
      {isProduct && !isMultiItem && (
        <Box sx={{
          mb: 4, p: 2.5, borderRadius: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          display: 'flex', alignItems: 'center', gap: 3
        }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              Unit Configuration
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.1rem' }}>
              {productUnitsPerCase} units/case
              {formData._enablePallets ? ` · ${productCasesPerPallet} cases/pallet` : ''}
            </Typography>
          </Box>
          {productTotalBase > 0 && (
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                Total Base Units
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main', fontSize: '1.1rem' }}>
                {productTotalBase.toLocaleString()} units
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* SINGLE ITEM — PRODUCT */}
      {!isMultiItem && isProduct && (
        <Grid container spacing={3}>
          {formData._enablePallets && (
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Pallets" type="number" value={formData.palletQty || ''}
                onChange={(e) => handleChange('palletQty', e.target.value)}
                inputProps={{ min: 0, step: 1 }} helperText={`1 pallet = ${productCasesPerPallet} cases`} sx={textFieldSx} />
            </Grid>
          )}
          {formData._enableCases && (
            <Grid item xs={12} md={formData._enablePallets ? 4 : 6}>
              <TextField fullWidth label="Cases" type="number" value={formData.caseQty || ''}
                onChange={(e) => handleChange('caseQty', e.target.value)}
                inputProps={{ min: 0, step: 1 }} helperText={`1 case = ${productUnitsPerCase} units`} sx={textFieldSx} />
            </Grid>
          )}
          <Grid item xs={12} md={formData._enablePallets ? 4 : (formData._enableCases ? 6 : 12)}>
            <TextField fullWidth label="Units *" type="number" value={formData.unitQty || ''}
              onChange={(e) => handleChange('unitQty', e.target.value)}
              inputProps={{ min: 0, step: 1 }} helperText="Individual units" required sx={textFieldSx} />
          </Grid>
          {(adjustmentType === 'add' || adjustmentType === 'transfer') && (
            <>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Lot Number *" value={formData.lotNumber || ''}
                  onChange={(e) => handleChange('lotNumber', e.target.value)} required sx={textFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Batch Number *" value={formData.batchNumber || ''}
                  onChange={(e) => handleChange('batchNumber', e.target.value)} required sx={textFieldSx} />
              </Grid>
            </>
          )}
          {adjustmentType === 'add' && (
            <>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="Expiration Date"
                    value={formData.expiryDate ? dayjs(formData.expiryDate) : null}
                    onChange={(v) => handleChange('expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="Manufacturing Date"
                    value={formData.mfgDate ? dayjs(formData.mfgDate) : null}
                    onChange={(v) => handleChange('mfgDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Vendor" value={formData.vendor ? formData.vendor.name : ''}
                  onClick={() => setVendorDialogOpen(true)} placeholder="Select Vendor"
                  InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
                  sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { ...textFieldSx['& .MuiOutlinedInput-root'], cursor: 'pointer' }, '& .MuiInputBase-input': { cursor: 'pointer' } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Invoice Number" value={formData.invoiceNumber || ''}
                  onChange={(e) => handleChange('invoiceNumber', e.target.value)} sx={textFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="Invoice Date" value={formData.invoiceDate ? dayjs(formData.invoiceDate) : null}
                    onChange={(v) => handleChange('invoiceDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="PO Number" value={formData.poNumber || ''}
                  onChange={(e) => handleChange('poNumber', e.target.value)} sx={textFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="PO Date" value={formData.poDate ? dayjs(formData.poDate) : null}
                    onChange={(v) => handleChange('poDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Notes" value={formData.notes || ''}
              onChange={(e) => handleChange('notes', e.target.value)}
              helperText="Optional additional information" sx={textFieldSx} />
          </Grid>
        </Grid>
      )}

      {/* SINGLE ITEM — NON-PRODUCT: lot cards */}
      {!isMultiItem && !isProduct && (
        <>
          {/* Per-lot cards */}
          {(formData.nonProductLots || []).map((lot, idx) => {
            const lots = formData.nonProductLots || [];
            const lotMode = lot.quantityMode || 'container'; // 'container' | 'measure'
            const lotQtyNum = parseFloat(lot.quantity) || 0;
            const calcTotal = isChemical && chemUnitQtyPerUnit && lotMode === 'container' && lotQtyNum > 0
              ? (lotQtyNum * chemUnitQtyPerUnit).toFixed(3) : null;
            const calcContainers = isChemical && chemUnitQtyPerUnit && lotMode === 'measure' && lotQtyNum > 0
              ? (lotQtyNum / chemUnitQtyPerUnit).toFixed(3) : null;
            return (
              <Box key={idx} sx={{
                mb: 2, p: 2.5, borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                bgcolor: alpha(theme.palette.grey[50], 0.5)
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.78rem' }}>
                    Lot Entry{lots.length > 1 ? ` #${idx + 1}` : ''}
                  </Typography>
                  {lots.length > 1 && (
                    <IconButton size="small" onClick={() => removeNonProductLot(idx)}
                      sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha('#d32f2f', 0.08) } }}>
                      <DeleteIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  {/* Chemical quantity mode toggle */}
                  {isChemical && chemUnitQtyPerUnit && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button size="small"
                          variant={lotMode === 'container' ? 'contained' : 'outlined'}
                          onClick={() => updateNonProductLot(idx, 'quantityMode', 'container')}
                          sx={{ borderRadius: 1.5, fontSize: '0.78rem', py: 0.5, textTransform: 'none' }}>
                          Enter by {singularUnitType(chemUnitType)}
                        </Button>
                        <Button size="small"
                          variant={lotMode === 'measure' ? 'contained' : 'outlined'}
                          onClick={() => updateNonProductLot(idx, 'quantityMode', 'measure')}
                          sx={{ borderRadius: 1.5, fontSize: '0.78rem', py: 0.5, textTransform: 'none' }}>
                          Enter by {chemUnitMeasure || 'Measure'}
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth
                      label={
                        isChemical && chemUnitQtyPerUnit
                          ? (lotMode === 'measure' ? `Total (${chemUnitMeasure}) *` : `Quantity * (${chemUnitType})`)
                          : 'Quantity *'
                      }
                      type="number" value={lot.quantity}
                      onChange={e => updateNonProductLot(idx, 'quantity', e.target.value)}
                      inputProps={{ min: 0, step: '0.0001' }}
                      helperText={
                        isChemical && chemUnitQtyPerUnit
                          ? `1 ${singularUnitType(chemUnitType)} = ${chemUnitQtyPerUnit}${chemUnitMeasure}`
                          : undefined
                      }
                      required sx={textFieldSx} />
                  </Grid>
                  {isChemical && chemUnitQtyPerUnit && lotQtyNum > 0 && (
                    <Grid item xs={12} md={6}>
                      {lotMode === 'measure' ? (
                        <TextField fullWidth label={`${singularUnitType(chemUnitType)} Count`}
                          value={calcContainers}
                          InputProps={{ readOnly: true }} helperText="Auto-calculated"
                          sx={{ ...textFieldSx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }} />
                      ) : calcTotal ? (
                        <TextField fullWidth label={`Total (${chemUnitMeasure})`} value={calcTotal}
                          InputProps={{ readOnly: true }} helperText="Auto-calculated"
                          sx={{ ...textFieldSx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }} />
                      ) : null}
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Unit Cost *" type="number" value={lot.unitCost}
                      onChange={e => updateNonProductLot(idx, 'unitCost', e.target.value)}
                      inputProps={{ min: 0, step: '0.01' }} required sx={textFieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Lot Number *" value={lot.lotNumber}
                      onChange={e => updateNonProductLot(idx, 'lotNumber', e.target.value)} required sx={textFieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Batch Number *" value={lot.batchNumber}
                      onChange={e => updateNonProductLot(idx, 'batchNumber', e.target.value)} required sx={textFieldSx} />
                  </Grid>
                  {isChemical && (
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Expiry Date" value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
                          onChange={v => updateNonProductLot(idx, 'expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                          slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                      </LocalizationProvider>
                    </Grid>
                  )}
                </Grid>
              </Box>
            );
          })}

          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={addNonProductLot}
            sx={{ mb: 3, fontWeight: 500, color: 'primary.main', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
            Add Another Lot
          </Button>

          {/* Shared vendor/invoice/notes */}
          {adjustmentType === 'add' && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Vendor" value={formData.vendor ? formData.vendor.name : ''}
                  onClick={() => setVendorDialogOpen(true)} placeholder="Select Vendor"
                  InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
                  sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { ...textFieldSx['& .MuiOutlinedInput-root'], cursor: 'pointer' }, '& .MuiInputBase-input': { cursor: 'pointer' } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Invoice Number" value={formData.invoiceNumber || ''}
                  onChange={(e) => handleChange('invoiceNumber', e.target.value)} sx={textFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="Invoice Date" value={formData.invoiceDate ? dayjs(formData.invoiceDate) : null}
                    onChange={(v) => handleChange('invoiceDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="PO Number" value={formData.poNumber || ''}
                  onChange={(e) => handleChange('poNumber', e.target.value)} sx={textFieldSx} />
              </Grid>
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker label="PO Date" value={formData.poDate ? dayjs(formData.poDate) : null}
                    onChange={(v) => handleChange('poDate', v ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={3} label="Notes" value={formData.notes || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  helperText="Optional additional information" sx={textFieldSx} />
              </Grid>
            </Grid>
          )}
        </>
      )}

      {/* MULTIPLE ITEMS */}
      {isMultiItem && (
        <Box>
          {/* Multi-item PRODUCTS: table */}
          {isProduct && (
            <>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>Configure each item</Typography>
              <Paper elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`, borderRadius: 2, mb: 4 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 500 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Units *</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Lot *</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>Batch *</TableCell>
                      {adjustmentType === 'add' && <TableCell sx={{ fontWeight: 500 }}>Expiry Date</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedItems.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField size="small" type="number"
                            value={itemsConfig[item.id]?.quantity || ''}
                            onChange={(e) => handleItemConfigChange(item.id, 'quantity', e.target.value)}
                            inputProps={{ min: 0, step: 1 }} required sx={{ width: 120 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small"
                            value={itemsConfig[item.id]?.lotNumber || ''}
                            onChange={(e) => handleItemConfigChange(item.id, 'lotNumber', e.target.value)}
                            required sx={{ width: 150 }} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small"
                            value={itemsConfig[item.id]?.batchNumber || ''}
                            onChange={(e) => handleItemConfigChange(item.id, 'batchNumber', e.target.value)}
                            required sx={{ width: 150 }} />
                        </TableCell>
                        {adjustmentType === 'add' && (
                          <TableCell>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                value={itemsConfig[item.id]?.expiryDate ? dayjs(itemsConfig[item.id].expiryDate) : null}
                                onChange={(v) => handleItemConfigChange(item.id, 'expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                                slotProps={{ textField: { size: 'small', sx: { width: 160 } } }} />
                            </LocalizationProvider>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}

          {/* Multi-item NON-PRODUCTS: item cards with per-item multi-lot */}
          {!isProduct && (
            <>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>Configure stock for each item</Typography>
              {selectedItems.map((item) => {
                const itemCfg = itemsConfig[item.id] || { lots: [] };
                const itemLots = itemCfg.lots || [];
                const itemUnitQtyPerUnit = isChemical ? parseFloat(item.primary_unit_quantity) : null;
                const itemUnitType = isChemical ? (item.primary_unit_type || 'units') : null;
                const itemUnitMeasure = isChemical ? (item.primary_unit_measure || '') : null;

                return (
                  <Box key={item.id} sx={{
                    mb: 3, p: 2.5, borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02)
                  }}>
                    {/* Item header */}
                    <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.7)}` }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>{item.name}</Typography>
                      {isChemical && itemUnitQtyPerUnit && (
                        <Typography variant="caption" color="text.secondary">
                          {itemUnitQtyPerUnit}{itemUnitMeasure} per {itemUnitType}
                        </Typography>
                      )}
                    </Box>

                    {/* Lot entries for this item */}
                    {itemLots.map((lot, lotIdx) => {
                      const iLotMode = lot.quantityMode || 'container';
                      const iLotQty = parseFloat(lot.quantity) || 0;
                      const calcTotal = isChemical && itemUnitQtyPerUnit && iLotMode === 'container' && iLotQty > 0
                        ? (iLotQty * itemUnitQtyPerUnit).toFixed(3) : null;
                      const calcContainersMulti = isChemical && itemUnitQtyPerUnit && iLotMode === 'measure' && iLotQty > 0
                        ? (iLotQty / itemUnitQtyPerUnit).toFixed(3) : null;
                      return (
                        <Box key={lotIdx} sx={{
                          mb: 2, p: 2, borderRadius: 1.5,
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                          bgcolor: alpha(theme.palette.grey[50], 0.6)
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.78rem' }}>
                              Lot Entry{itemLots.length > 1 ? ` #${lotIdx + 1}` : ''}
                            </Typography>
                            {itemLots.length > 1 && (
                              <IconButton size="small" onClick={() => removeItemLot(item.id, lotIdx)}
                                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: alpha('#d32f2f', 0.08) } }}>
                                <DeleteIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            )}
                          </Box>
                          <Grid container spacing={2}>
                            {/* Chemical quantity mode toggle */}
                            {isChemical && itemUnitQtyPerUnit && (
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Button size="small"
                                    variant={iLotMode === 'container' ? 'contained' : 'outlined'}
                                    onClick={() => updateItemLot(item.id, lotIdx, 'quantityMode', 'container')}
                                    sx={{ borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, textTransform: 'none' }}>
                                    Enter by {singularUnitType(itemUnitType)}
                                  </Button>
                                  <Button size="small"
                                    variant={iLotMode === 'measure' ? 'contained' : 'outlined'}
                                    onClick={() => updateItemLot(item.id, lotIdx, 'quantityMode', 'measure')}
                                    sx={{ borderRadius: 1.5, fontSize: '0.75rem', py: 0.4, textTransform: 'none' }}>
                                    Enter by {itemUnitMeasure || 'Measure'}
                                  </Button>
                                </Box>
                              </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <TextField fullWidth size="small"
                                label={
                                  isChemical && itemUnitQtyPerUnit
                                    ? (iLotMode === 'measure' ? `Total (${itemUnitMeasure}) *` : `Quantity * (${itemUnitType})`)
                                    : 'Quantity *'
                                }
                                type="number" value={lot.quantity}
                                onChange={e => updateItemLot(item.id, lotIdx, 'quantity', e.target.value)}
                                inputProps={{ min: 0, step: '0.0001' }}
                                helperText={isChemical && itemUnitQtyPerUnit ? `1 ${singularUnitType(itemUnitType)} = ${itemUnitQtyPerUnit}${itemUnitMeasure}` : undefined}
                                required sx={textFieldSx} />
                            </Grid>
                            {isChemical && itemUnitQtyPerUnit && iLotQty > 0 && (
                              <Grid item xs={12} md={6}>
                                {iLotMode === 'measure' ? (
                                  <TextField fullWidth size="small" label={`${singularUnitType(itemUnitType)} Count`}
                                    value={calcContainersMulti}
                                    InputProps={{ readOnly: true }} helperText="Auto-calculated"
                                    sx={{ ...textFieldSx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }} />
                                ) : calcTotal ? (
                                  <TextField fullWidth size="small" label={`Total (${itemUnitMeasure})`} value={calcTotal}
                                    InputProps={{ readOnly: true }} helperText="Auto-calculated"
                                    sx={{ ...textFieldSx, '& .MuiInputBase-input': { color: 'success.main', fontWeight: 500 } }} />
                                ) : null}
                              </Grid>
                            )}
                            <Grid item xs={12} md={6}>
                              <TextField fullWidth size="small" label="Unit Cost *" type="number" value={lot.unitCost}
                                onChange={e => updateItemLot(item.id, lotIdx, 'unitCost', e.target.value)}
                                inputProps={{ min: 0, step: '0.01' }} required sx={textFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField fullWidth size="small" label="Lot Number *" value={lot.lotNumber}
                                onChange={e => updateItemLot(item.id, lotIdx, 'lotNumber', e.target.value)}
                                required sx={textFieldSx} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField fullWidth size="small" label="Batch Number *" value={lot.batchNumber}
                                onChange={e => updateItemLot(item.id, lotIdx, 'batchNumber', e.target.value)}
                                required sx={textFieldSx} />
                            </Grid>
                            {isChemical && (
                              <Grid item xs={12} md={6}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                  <DatePicker label="Expiry Date"
                                    value={lot.expiryDate ? dayjs(lot.expiryDate) : null}
                                    onChange={v => updateItemLot(item.id, lotIdx, 'expiryDate', v ? v.format('YYYY-MM-DD') : '')}
                                    slotProps={{ textField: { fullWidth: true, size: 'small', sx: textFieldSx } }} />
                                </LocalizationProvider>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      );
                    })}

                    <Button size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />} onClick={() => addItemLot(item)}
                      sx={{ fontWeight: 500, color: 'primary.main', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}>
                      Add Another Lot
                    </Button>
                  </Box>
                );
              })}
            </>
          )}

          {/* Shared vendor/invoice/notes (applies to all items) */}
          {adjustmentType === 'add' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                Vendor & Invoice (Optional: applies to all items)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Vendor" value={formData.vendor ? formData.vendor.name : ''}
                    onClick={() => setVendorDialogOpen(true)} placeholder="Select Vendor"
                    InputProps={{ readOnly: true, sx: { cursor: 'pointer' } }}
                    sx={{ ...textFieldSx, '& .MuiOutlinedInput-root': { ...textFieldSx['& .MuiOutlinedInput-root'], cursor: 'pointer' }, '& .MuiInputBase-input': { cursor: 'pointer' } }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Invoice Number" value={formData.invoiceNumber || ''}
                    onChange={(e) => handleChange('invoiceNumber', e.target.value)} sx={textFieldSx} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label="Invoice Date" value={formData.invoiceDate ? dayjs(formData.invoiceDate) : null}
                      onChange={(v) => handleChange('invoiceDate', v ? v.format('YYYY-MM-DD') : '')}
                      slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="PO Number" value={formData.poNumber || ''}
                    onChange={(e) => handleChange('poNumber', e.target.value)} sx={textFieldSx} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker label="PO Date" value={formData.poDate ? dayjs(formData.poDate) : null}
                      onChange={(v) => handleChange('poDate', v ? v.format('YYYY-MM-DD') : '')}
                      slotProps={{ textField: { fullWidth: true, sx: textFieldSx } }} />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Notes" value={formData.notes || ''}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    helperText="Optional additional information (applies to all items)" sx={textFieldSx} />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      )}

      {/* Validation Messages */}
      {formData.quantity && parseFloat(formData.quantity) <= 0 && (
        <Typography variant="caption" color="error" sx={{ mt: 2, display: 'block' }}>
          Quantity must be greater than 0
        </Typography>
      )}

      {/* Vendor Selection Dialog */}
      <Dialog
        open={vendorDialogOpen}
        onClose={() => setVendorDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            width: '80vw',
            maxWidth: '1200px'
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 3,
          pt: 3,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
        }}>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>Select Vendor</Typography>
          <IconButton onClick={() => setVendorDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <TextField
            fullWidth
            placeholder="Search vendors..."
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ 
              mt: 3,
              mb: 4,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem'
              }
            }}
          />
          <Table sx={{ 
            '& .MuiTableCell-root': { 
              fontSize: '1rem',
              py: 2.5 
            } 
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 500 }}>Vendor Name</TableCell>
                <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSuppliers.map((supplier) => (
                <TableRow 
                  key={supplier.id} 
                  hover 
                  sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                >
                  <TableCell sx={{ fontSize: '0.95rem' }}>{supplier.name}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleChange('vendor', supplier);
                        setVendorDialogOpen(false);
                        setVendorSearch('');
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        fontSize: '0.95rem'
                      }}
                    >
                      Select
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredSuppliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography color="text.secondary" sx={{ py: 4, fontSize: '1rem' }}>
                      No vendors found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
